import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../contractorApi';

// ── アイコン ──────────────────────────────────────────────────
const CalendarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

// ── スタイル定数 ───────────────────────────────────────────────
const CARD = { background: '#fff', borderRadius: 8, padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };
const H2 = { fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 24px' };
const LBL = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 8 };

// ── メイン ────────────────────────────────────────────────────
const ScheduleCreatePage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
    }, [projectId]);

    const canSubmit = startDate && endDate && new Date(startDate) <= new Date(endDate);
    const endBeforeStart = startDate && endDate && new Date(startDate) > new Date(endDate);

    const duration = (() => {
        if (!canSubmit) return null;
        const diff = Math.abs(new Date(endDate) - new Date(startDate));
        return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
    })();

    const fmtShort = (val) => {
        try { return new Date(val).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }); }
        catch { return val; }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        setError(null);
        try {
            await api.post('schedules', {
                project_id: Number(projectId),
                start_date: startDate,
                end_date: endDate,
                message: message || null,
            });
            navigate(`/contractor/project/${projectId}`);
        } catch {
            setError('日程登録に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const customerName = project
        ? `${project.customer?.last_name || ''} ${project.customer?.first_name || ''}`.trim()
        : '';
    const workTypes = project?.condition?.work_types?.join('・') || '';
    const desiredPeriod = project?.condition?.desired_period || '';

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>
            <style>{`
                .sch-inp:focus { border-color: #005c53 !important; outline: none; }
                .sch-range { display: flex; align-items: center; justify-content: center; padding: 16px 0; animation: fadeIn 0.3s ease; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <div style={{ maxWidth: 768, margin: '0 auto', padding: '32px 24px' }}>

                {/* ページタイトル */}
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 32px' }}>
                    工事日程を登録する
                </h1>

                {/* 案件情報カード */}
                <div style={CARD}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ padding: 8, background: '#e3f2fd', borderRadius: 6, flexShrink: 0 }}>
                            <span style={{ color: '#1976d2' }}><InfoIcon /></span>
                        </div>
                        <div>
                            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' }}>案件情報</h2>
                            {customerName && (
                                <p style={{ color: '#6b7280', margin: '0 0 2px', fontSize: '0.9rem' }}>
                                    <span style={{ color: '#1a1a2e', fontWeight: 500 }}>顧客名:</span> {customerName} 様
                                </p>
                            )}
                            {workTypes && (
                                <p style={{ color: '#6b7280', margin: '0 0 2px', fontSize: '0.9rem' }}>
                                    <span style={{ color: '#1a1a2e', fontWeight: 500 }}>工事内容:</span> {workTypes}
                                </p>
                            )}
                            {desiredPeriod && (
                                <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9rem' }}>
                                    <span style={{ color: '#1a1a2e', fontWeight: 500 }}>希望時期:</span> {desiredPeriod}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>

                    {/* 工事期間カード */}
                    <div style={CARD}>
                        <h2 style={H2}>工事期間</h2>

                        {/* 開始日 */}
                        <div style={{ marginBottom: 24 }}>
                            <label style={LBL}>
                                工事開始日 <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none', display: 'flex' }}>
                                    <CalendarIcon />
                                </span>
                                <input
                                    className="sch-inp"
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    required
                                    style={{ width: '100%', paddingLeft: 44, paddingRight: 16, paddingTop: 12, paddingBottom: 12, border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.9rem', color: '#1a1a2e', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                                />
                            </div>
                        </div>

                        {/* 期間ビジュアル */}
                        {canSubmit && (
                            <div className="sch-range">
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>開始</div>
                                    <div style={{ padding: '6px 14px', background: '#005c53', color: '#fff', borderRadius: 4, fontSize: '0.85rem', fontWeight: 600 }}>
                                        {fmtShort(startDate)}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 16px' }}>
                                    <span style={{ color: '#005c53' }}><ArrowRightIcon /></span>
                                    <span style={{ fontSize: '0.75rem', color: '#005c53', fontWeight: 600, marginTop: 2 }}>{duration}日間</span>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>終了</div>
                                    <div style={{ padding: '6px 14px', background: '#005c53', color: '#fff', borderRadius: 4, fontSize: '0.85rem', fontWeight: 600 }}>
                                        {fmtShort(endDate)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 終了日 */}
                        <div>
                            <label style={LBL}>
                                工事終了日 <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none', display: 'flex' }}>
                                    <CalendarIcon />
                                </span>
                                <input
                                    className="sch-inp"
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    min={startDate || undefined}
                                    required
                                    style={{ width: '100%', paddingLeft: 44, paddingRight: 16, paddingTop: 12, paddingBottom: 12, border: `1px solid ${endBeforeStart ? '#ef4444' : '#e0e4ea'}`, borderRadius: 4, fontSize: '0.9rem', color: '#1a1a2e', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                                />
                            </div>
                            {endBeforeStart && (
                                <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: '8px 0 0' }}>
                                    終了日は開始日より後の日付を選択してください
                                </p>
                            )}
                        </div>
                    </div>

                    {/* メッセージカード */}
                    <div style={CARD}>
                        <label style={LBL}>顧客へのメッセージ（任意）</label>
                        <textarea
                            className="sch-inp"
                            rows={5}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder="工事開始前の準備や注意事項などをお伝えください"
                            style={{ width: '100%', padding: '12px 16px', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.9rem', color: '#1a1a2e', resize: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s', fontFamily: 'inherit' }}
                        />
                    </div>

                    {/* エラー */}
                    {error && <p style={{ color: '#ef4444', fontSize: '0.88rem', marginBottom: 12 }}>{error}</p>}

                    {/* 送信ボタン */}
                    <button
                        type="submit"
                        disabled={!canSubmit || loading}
                        style={{
                            width: '100%',
                            padding: '16px',
                            background: canSubmit ? '#005c53' : '#9ca3af',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: '1.05rem',
                            fontWeight: 600,
                            cursor: canSubmit && !loading ? 'pointer' : 'not-allowed',
                            transition: 'background 0.2s',
                        }}
                    >
                        {loading ? '登録中...' : '日程を登録する'}
                    </button>
                    <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', marginTop: 12 }}>
                        登録後、顧客に通知が送られます
                    </p>
                </form>
            </div>
        </div>
    );
};

export default ScheduleCreatePage;
